/**
 * InviteUserModal — T27.
 *
 * Opens as a Mantine Modal. Admins can invite writers and admins.
 * system_owners can also invite other system_owners.
 * On success, shows a one-time temp-password banner.
 * On 409 duplicate_email, shows an inline form error on the email field.
 */
import {
  Alert,
  Button,
  CopyButton,
  Group,
  Modal,
  Select,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { IconCheck, IconCopy } from '@tabler/icons-react';
import { useState } from 'react';
import { usersApi } from '../../api/endpoints/users';
import { ApiError } from '../../api/errors';
import { useAuthStore } from '../../state/auth-store';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Props {
  opened: boolean;
  onClose: () => void;
  /** Called after a successful invite (before the temp-password banner is shown). */
  onSuccess: () => void;
}

// ─── Role options ─────────────────────────────────────────────────────────────

const ADMIN_ROLE_OPTIONS = [
  { value: 'writer', label: 'writer' },
  { value: 'admin', label: 'admin' },
];

const SYSTEM_OWNER_ROLE_OPTIONS = [
  { value: 'writer', label: 'writer' },
  { value: 'admin', label: 'admin' },
  { value: 'system_owner', label: 'system_owner' },
];

// ─── Component ────────────────────────────────────────────────────────────────

export function InviteUserModal({ opened, onClose, onSuccess }: Props) {
  const user = useAuthStore((s) => s.user);

  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<string>('writer');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [tempPassword, setTempPassword] = useState<string | null>(null);

  const roleOptions =
    user?.role === 'system_owner' ? SYSTEM_OWNER_ROLE_OPTIONS : ADMIN_ROLE_OPTIONS;

  function reset() {
    setEmail('');
    setName('');
    setRole('writer');
    setEmailError(null);
    setSubmitting(false);
    setTempPassword(null);
  }

  function handleClose() {
    reset();
    onClose();
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setEmailError(null);
    setSubmitting(true);

    try {
      const result = (await usersApi.create({
        email,
        name,
        role: role as 'writer' | 'admin' | 'system_owner',
      })) as { ok: boolean; tempPassword?: string };

      setTempPassword(result.tempPassword ?? null);
      onSuccess();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setEmailError('A user with this email already exists.');
      } else {
        // Re-throw unexpected errors so they surface in the console
        throw err;
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal opened={opened} onClose={handleClose} title="Invite user" centered>
      {tempPassword ? (
        // ── Temp-password banner ───────────────────────────────────────────
        <Stack>
          <Alert data-testid="temp-password-banner" color="green" title="User invited">
            <Group gap="xs" wrap="nowrap" align="center">
              <Text span>Temporary password:</Text>
              <CopyButton value={tempPassword}>
                {({ copied, copy }) => (
                  <Button
                    data-testid="copy-temp-password"
                    size="compact-xs"
                    variant="light"
                    leftSection={copied ? <IconCheck size={12} /> : <IconCopy size={12} />}
                    onClick={copy}
                  >
                    {copied ? 'Copied' : 'Copy'}
                  </Button>
                )}
              </CopyButton>
              <Text span fw={600}>
                {tempPassword}
              </Text>
            </Group>
            <Text size="sm" mt="xs">
              Share this with the user out-of-band — it will not be shown again.
            </Text>
          </Alert>
          <Button variant="default" onClick={handleClose}>
            Close
          </Button>
        </Stack>
      ) : (
        // ── Invite form ────────────────────────────────────────────────────
        <form onSubmit={handleSubmit}>
          <Stack>
            <div>
              <TextInput
                data-testid="invite-email"
                label="Email"
                type="email"
                required
                value={email}
                onChange={(e) => {
                  setEmail(e.currentTarget.value);
                  setEmailError(null);
                }}
                error={
                  emailError ? (
                    <span data-testid="invite-email-error">{emailError}</span>
                  ) : undefined
                }
                autoComplete="off"
              />
            </div>

            <TextInput
              data-testid="invite-name"
              label="Name"
              required
              value={name}
              onChange={(e) => setName(e.currentTarget.value)}
              autoComplete="off"
            />

            <Select
              data-testid="invite-role"
              label="Role"
              required
              data={roleOptions}
              value={role}
              onChange={(val) => setRole(val ?? 'writer')}
            />

            <Group justify="flex-end">
              <Button
                data-testid="invite-cancel"
                variant="default"
                onClick={handleClose}
                type="button"
              >
                Cancel
              </Button>
              <Button data-testid="invite-submit" type="submit" loading={submitting}>
                Invite
              </Button>
            </Group>
          </Stack>
        </form>
      )}
    </Modal>
  );
}
